import getLocal from '@/app/utils/getLocal';
import { FormatMoneyLocal } from '@elem/ui-common/utils/FormatMoneyLocal';
import { getSubCardsForTime } from '@elem/ui-common/utils/getSubCardsForTime';
import { hideCardForTime } from '@elem/ui-common/utils/hideCardForTime';
import classNames from 'classnames';
import editorEventsBus from 'lowcode/utils/editorEventsBus';
import React, { useState } from 'react';
import EfBundlesCalculator from '../ef-bundles-calculator';
import EfCoupon from '../ef-coupon';
import EfIcon from '../ef-icon';
import EfImg from '../ef-img';
import EfLink from '../ef-link';
import './index.scss';
import { langText } from './langText';

interface EfBundlesCardItemSubProps {
    /**
     * 子产品-图片
     */
    img?: string;
    /**
     * 子产品所属父产品的variantId（避免加减数量时混乱）
     */
    parentVariantId?: string;
    /**
     * 子产品-产品 productId
     */
    productId: string;
    /**
     * 子产品-产品 variantId
     */
    variantId: string;
    /**
     * 子产品-实际价格
     */
    discountPrice: number;
    /**
     * 子产品-划线价格
     */
    originalPrice: number;
    /**
     * 子产品-默认购买数量
     */
    quantity?: number;
    /**
     * 子产品-库存
     */
    inventoryQuantity?: number;
    /**
     * 子产品-开始展示时间
     */
    timeStart?: string;
    /**
     * 子产品-结束展示时间
     */
    timeEnd?: string;
    /**
     * 文案-主标题
     */
    name?: string;
}

export interface EfBundlesCardProps {
    /**
     * 主图
     */
    img?: string;
    /**
     * 产品 productId
     */
    productId: string;
    /**
     * 产品 variantId
     */
    variantId: string;
    /**
     * 购买链接
     */
    buyHref?: string;
    /**
     * 产品实际价格
     */
    discountPrice: number;
    /**
     * 产品划线价格
     */
    originalPrice: number;
    /**
     * 默认购买数量
     */
    quantity: number;
    /**
     * 库存
     */
    inventoryQuantity?: number;
    /**
     * 产品开始展示时间
     */
    timeStart?: string;
    /**
     * 产品结束展示时间
     */
    timeEnd?: string;
    /**
     * 优惠券-优惠码
     */
    coupon?: string;
    /**
     * 优惠券-左侧提示文案
     */
    couponText?: string;
    /**
     * 优惠券-优惠浮窗提示语
     */
    couponTips?: string;
    /**
     * 是否点击后添加到购物车
     */
    buyInCart?: boolean;
    /**
     * buy 不直接触发,回到回调中
     */
    isBuyInCallback?: boolean;
    /**
     * isBuyInCallback 的回调
     */
    buyLinkCallback?: (item: any, buyHref: string) => void;
    /**
     * 文案-大标题
     */
    title?: string;
    /**
     * 文案-主标题
     */
    name?: string;
    /**
     * 文案-特性
     */
    power?: string;
    /**
     * 文案-描述
     */
    desc?: string;
    /**
     * 更多详情链接
     */
    moreLink?: string;
    /**
     * buy now 按钮自定义文案
     */
    buyText?: string;
    /**
     * buy now 按钮自定义回调
     */
    callbackBuy?: any;
    /**
     * 子产品集
     */
    subs?: EfBundlesCardItemSubProps[];
    /**
     * 用于埋点的list_name
     */
    list_name?: string;
    /**
     * 埋点-learn more
     */
    gtm_learn_more?: any;
    /**
     * 样式名
     */
    className?: string;
    /**
     * also like text
     */
    alsoLikeText?: string;
    /**
     * 自定义分期的icon
     */
    affirmIconUrl?: string;
    /**
     * 是否需要Affirm的图标和文案
     */
    hasAffirm?: boolean;
    /**
     * 传入的Affirm文案
     */
    affirmText?: string;
}

/**
 * 维护:谢保林
 *
 * 用于 Bundles 的商品卡片
 */
const EfBundlesCard: React.FC<EfBundlesCardProps> = ({
    img,
    productId,
    variantId,
    buyHref,
    discountPrice,
    originalPrice,
    quantity,
    inventoryQuantity,
    timeStart,
    timeEnd,
    coupon,
    couponText,
    couponTips,
    buyInCart,
    title,
    name,
    power,
    desc,
    moreLink,
    gtm_learn_more,
    isBuyInCallback,
    buyLinkCallback,
    buyText,
    callbackBuy,
    subs,
    list_name,
    className,
    alsoLikeText = '',
    affirmIconUrl,
    hasAffirm,
    affirmText = '',
}) => {
    const item = {
        img,
        productId,
        variantId,
        buyHref,
        discountPrice,
        originalPrice,
        quantity,
        inventoryQuantity,
        timeStart,
        timeEnd,
        coupon,
        couponText,
        couponTips,
        buyInCart,
        title,
        name,
        power,
        desc,
        moreLink,
        subs,
        buyText,
        callbackBuy,
        isBuyInCallback,
    };

    if (!variantId) {
        return null;
    }
    // 如果有传入(有效时间)
    if (hideCardForTime(item.timeStart, item.timeEnd)) {
        return null;
    }
    const { locale = 'us' } = getLocal();
    const textA = langText[locale] || langText['us']; //卡片内非传入文本
    const prefixCls = `ef-bundles-card`;
    const classes = classNames(prefixCls, {
        [`${className}`]: className,
    });
    const subs1 = getSubCardsForTime(item.subs || []); // 子元素群
    const [subsB, setSubsB] = useState(subs1); //更多购买-用于保存子级购买数量
    const hasSubs = subs1.length > 0; //是否展示更多购买
    const defaultQuantity = 0; //默认购买数量
    const [totalBundle, setTotalBundle] = useState({
        discount: item.discountPrice,
        original: item.originalPrice,
    }); //卡片总价
    const inventoryQuantity2 = item.inventoryQuantity || 0; //主产品的剩余库存
    const [buyHref2, setBuyHref2] = useState(`https://${locale}.ecoflow.com/cart/${item.variantId}:1?channel=buy_button`); //购买链接
    const [subProductName, setSubProductName] = useState(''); //选择的子产品;用于发送埋点
    // 设置购买链接
    const setBuyHrefFn = (subsB2: any) => {
        let valStr = ''; // 副产品
        let subProductName2 = '';
        for (let i = 0; i < subsB2.length; i++) {
            const itemB = subsB2[i];
            const variantId = itemB.variantId;
            const quantity = itemB.quantity;
            if (variantId && quantity) {
                valStr += `,${variantId}:${quantity}`;
                subProductName2 += `_${itemB.name}`;
            }
        }
        setSubProductName(subProductName2); //用于拼接选择的子产品标题
        const href1 = `${item.variantId}:1`; //主产品
        const hrefAll = `https://${locale}.ecoflow.com/cart/${href1}${valStr}?channel=buy_button`;
        setBuyHref2(hrefAll);
    };
    // 数量发生变化
    const numChange = (index2: number, quantity: number) => {
        const subsB2 = JSON.parse(JSON.stringify(subsB));
        subsB2[index2].quantity = quantity || 0;
        const totalObj = getSubTotalFn(subsB2);
        setBuyHrefFn(subsB2);
        setTotalBundle({
            discount: (item.discountPrice || 0) + totalObj.discount,
            original: (item.originalPrice || 0) + totalObj.original,
        });
        setSubsB(subsB2);
    };
    // 获取子项的总价
    const getSubTotalFn = (subs2: any) => {
        let discount = 0;
        let original = 0;
        for (let i = 0; i < subs2.length; i++) {
            const item2 = subs2[i];
            const quantity = item2.quantity || 0;

            discount += quantity * (item2.discountPrice || 0);
            original += quantity * (item2.originalPrice || 0);
        }

        return {
            discount,
            original,
        };
    };
    // 点击购买--打开购物车
    const buyInCartHandle = () => {
        editorEventsBus.emit(
            'efshopingcart-add',
            [
                {
                    item: item,
                    quantity: 1,
                },
            ],
            (list: any) => {
                //console.log('list', list);
            },
        );
    };

    const gtm_learn_more2 = gtm_learn_more || {
        event: 'uaEvent',
        event_name: 'select_product',
        list_name: list_name || item.name || '',
        product_name: item.name || '',
        button_name: textA.link_more,
    };

    return (
        <div className={classes}>
            {item.title && <p className={`${prefixCls}-big-title`}>{item.title}</p>}
            <div className={`${prefixCls}-img`}>
                <EfImg pc={item.img} />
            </div>
            {item.name && <p className={`${prefixCls}-title`}>{item.name}</p>}
            {item.power && <p className={`${prefixCls}-subTitle`}>{item.power}</p>}
            {item.desc && <p className={`${prefixCls}-desc`}>{item.desc}</p>}
            {item.coupon && item.couponText && (
                <>
                    <EfCoupon value={item.coupon} couponText={item.couponText} couponTips={item.couponTips} />
                </>
            )}
            {hasSubs && (
                <>
                    <div className={`${prefixCls}-also-like`}>{alsoLikeText || textA.also_like}</div>
                    <div className={`${prefixCls}-subs`}>
                        {subsB.map((sub: EfBundlesCardItemSubProps, index2: number) => {
                            return (
                                <div key={index2} className={`${prefixCls}-sub-item`}>
                                    <div className={`${prefixCls}-sub-item-img`}>
                                        <EfImg pc={sub.img} />
                                    </div>
                                    <div className={`${prefixCls}-sub-item-msg`}>
                                        {sub?.name && <p className={`${prefixCls}-sub-item-title`}>{sub.name}</p>}
                                        {sub?.desc && <p className={`${prefixCls}-sub-item-desc`}>{sub.desc}</p>}
                                        <div className={`${prefixCls}-sub-item-control`}>
                                            {sub.discountPrice && (
                                                <p className={`${prefixCls}-sub-item-price`}>
                                                    <span className={`${prefixCls}-sub-item-price-discount`}>
                                                        {FormatMoneyLocal(sub?.discountPrice, locale)}
                                                    </span>
                                                    {sub?.discountPrice !== sub?.originalPrice && (
                                                        <span className={`${prefixCls}-sub-item-price-original`}>
                                                            {FormatMoneyLocal(sub?.originalPrice, locale)}
                                                        </span>
                                                    )}
                                                </p>
                                            )}
                                            <EfBundlesCalculator
                                                defaultQuantity={defaultQuantity}
                                                item={sub}
                                                callback={(quantity: number) => {
                                                    numChange(index2, quantity);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
            <div className={`${prefixCls}-footer`}>
                <div className={`${prefixCls}-total`}>
                    <span className={`${prefixCls}-total-discount`}>{FormatMoneyLocal(totalBundle.discount || 0, locale)}</span>
                    {totalBundle.discount !== totalBundle.original && (
                        <span className={`${prefixCls}-total-original`}>{FormatMoneyLocal(totalBundle.original || 0, locale)}</span>
                    )}
                </div>
                {hasAffirm && (
                    <div className={`${prefixCls}-affirm`}>
                        <EfIcon
                            className={`${prefixCls}-affirm-icon`}
                            pc={affirmIconUrl ? affirmIconUrl : 'https://ecoflow-service-us-cdn.ecoflow.com/upload/image/1718366240658/affirm.png'}
                        />
                        <span className={`${prefixCls}-affirm-text`}>{affirmText || textA.affirm_text}</span>
                    </div>
                )}
                <div className={`${prefixCls}-links`}>
                    {item.moreLink && (
                        <EfLink className={`${prefixCls}-link ${prefixCls}-link-more`} href={item.moreLink} dataLayer={gtm_learn_more2}>
                            {textA.link_more}
                        </EfLink>
                    )}
                    {inventoryQuantity2 > 0 && !item.buyInCart && (
                        <>
                            {buyText && callbackBuy ? (
                                <span className={`${prefixCls}-link ${prefixCls}-link-buy  ${prefixCls}-link-disabled`}>{buyText || textA.link_buy}</span>
                            ) : (
                                <EfLink
                                    className={`${prefixCls}-link ${prefixCls}-link-buy`}
                                    href={isBuyInCallback ? undefined : buyHref || buyHref2}
                                    onClick={() => {
                                        isBuyInCallback && buyLinkCallback(item, buyHref || buyHref2);
                                    }}
                                    dataLayer={{
                                        event: 'uaEvent',
                                        event_name: 'checkout',
                                        button_name: `${textA.link_buy}_${item?.name}${subProductName}`,
                                    }}
                                >
                                    {buyText || textA.link_buy}
                                </EfLink>
                            )}
                        </>
                    )}
                    {inventoryQuantity2 > 0 && item.buyInCart && (
                        <button className={`${prefixCls}-link ${prefixCls}-link-buy`} onClick={buyInCartHandle}>
                            {textA.link_buy}
                        </button>
                    )}
                    {inventoryQuantity2 <= 0 && <span className={`${prefixCls}-link ${prefixCls}-link-buy ${prefixCls}-link-disabled`}>{textA.sold_out}</span>}
                </div>
            </div>
        </div>
    );
};

export default EfBundlesCard;
